import { useState, useEffect } from 'react'
import { Trophy, Calendar, MapPin, Users, Award, List, TrendingUp } from 'lucide-react'
import CategoryView from './CategoryView'

const STORAGE_KEY = 'tournament-viewer-selected-category'

function TournamentView({ data, theme, onRefresh }) {
  // Initialize from localStorage if available
  const [selectedCategoryId, setSelectedCategoryId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY)
    } catch {
      return null
    }
  })
  
  const { tournament, categories, exportedAt } = data
  
  // Save to localStorage whenever selection changes
  useEffect(() => {
    try {
      if (selectedCategoryId) {
        localStorage.setItem(STORAGE_KEY, selectedCategoryId)
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch (err) {
      console.error('Failed to save category selection:', err)
    }
  }, [selectedCategoryId])
  
  // Get the current category from the latest data
  // Simply find it from the categories array - no deep clone needed
  const categoryToShow = selectedCategoryId 
    ? categories.find(c => c.id === selectedCategoryId)
    : null

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    } catch {
      return 'Invalid date'
    }
  }

  // Show CategoryView if we have a selected ID and a category to show
  if (selectedCategoryId && categoryToShow) {
    const viewKey = `category-${selectedCategoryId}-${exportedAt}`
    console.log('=== RENDERING CATEGORYVIEW ===')
    console.log('Key:', viewKey)
    console.log('exportedAt being passed:', exportedAt)
    console.log('category.matches.length:', categoryToShow.matches?.length)
    console.log('First match in categoryToShow:', {
      id: categoryToShow.matches?.[0]?.id,
      status: categoryToShow.matches?.[0]?.status,
      team1Score: categoryToShow.matches?.[0]?.team1Score,
      team2Score: categoryToShow.matches?.[0]?.team2Score
    })
    
    return (
      <CategoryView
        key={viewKey}
        category={categoryToShow}
        tournament={tournament}
        theme={theme}
        exportedAt={exportedAt}
        onRefresh={onRefresh}
        onBack={() => setSelectedCategoryId(null)}
      />
    )
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.gradient} pb-20`}>
      {/* Header */}
      <div className={`bg-white shadow-lg sticky top-0 z-10 border-b-4 ${theme.border.primary}`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-start gap-3 sm:gap-4">
            {tournament.logoUrl ? (
              <img
                src={tournament.logoUrl}
                alt="Tournament Logo"
                className={`w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 object-contain rounded-lg ${theme.primaryShades[50]} p-2 border-2 ${theme.border.primary} flex-shrink-0`}
                onError={(e) => e.target.style.display = 'none'}
              />
            ) : (
              <div className={`w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br ${theme.gradient} rounded-lg flex items-center justify-center shadow-lg flex-shrink-0`}>
                <Trophy className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" />
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg sm:text-xl md:text-2xl font-black text-gray-800 leading-tight">
                  {tournament.name}
                </h1>
                {theme.name === 'Breast Cancer Awareness' && (
                  <svg className={`w-6 h-6 ${theme.text.primary}`} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C10.5 2 9.19 2.95 8.5 4.26C7.81 2.95 6.5 2 5 2C2.79 2 1 3.79 1 6C1 8.5 2.5 10.5 4.5 12.5L8.5 16.5C9.88 17.88 11.88 19 12 19C12.12 19 14.12 17.88 15.5 16.5L19.5 12.5C21.5 10.5 23 8.5 23 6C23 3.79 21.21 2 19 2C17.5 2 16.19 2.95 15.5 4.26C14.81 2.95 13.5 2 12 2M12 5.5C12.83 5.5 13.5 6.17 13.5 7C13.5 7.83 12.83 8.5 12 8.5C11.17 8.5 10.5 7.83 10.5 7C10.5 6.17 11.17 5.5 12 5.5Z"/>
                  </svg>
                )}
              </div>
              <div className="mt-1 text-xs space-y-0.5">
                <div className={`${theme.text.primary} font-bold`}>{theme.ribbon} {theme.message}</div>
                <div className="text-gray-500 font-semibold">Powered by PickleStack</div>
              </div>
              {tournament.description && (
                <p className="text-gray-600 text-sm mt-2 whitespace-pre-wrap hidden md:block">
                  {tournament.description}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tournament Info */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Tournament Details</h2>
          <div className="grid grid-cols-2 gap-4">
            {tournament.location && (
              <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500 font-semibold">Location</p>
                  <p className="text-sm font-bold text-gray-800">{tournament.location}</p>
                </div>
              </div>
            )}
            
            {tournament.startDate && (
              <div className="flex items-start gap-2">
                <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className={`text-xs ${theme.text.primary} font-semibold`}>Start Date</p>
                  <p className={`text-sm font-bold ${theme.text.primary}`}>{formatDate(tournament.startDate)}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-start gap-2">
              <Users className={`w-5 h-5 ${theme.text.primary} flex-shrink-0 mt-0.5`} />
              <div>
                <p className={`text-xs ${theme.text.primary} font-semibold`}>Categories</p>
                <p className={`text-sm font-bold ${theme.text.primary}`}>{categories.length}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <Trophy className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500 font-semibold">Total Matches</p>
                <p className="text-sm font-bold text-gray-800">
                  {categories.reduce((sum, cat) => sum + cat.matches.length, 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-4">
          <h2 className="text-2xl font-black text-white mb-4 px-2">Categories</h2>
          
          {categories.map((category) => {
            const completedMatches = category.matches.filter(m => m.status === 'completed').length
            const totalMatches = category.matches.length
            const progress = totalMatches > 0 ? (completedMatches / totalMatches) * 100 : 0

            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategoryId(category.id)}
                className={`w-full bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all transform hover:scale-[1.02] text-left border-2 ${theme.border.primary} hover:${theme.border.primaryHover} animate-content-update`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-black text-gray-800 mb-1">
                      {category.title}
                    </h3>
                    {category.description && (
                      <p className="text-sm text-gray-600 whitespace-pre-wrap hidden md:block">
                        {category.description}
                      </p>
                    )}
                  </div>
                  <div className={`bg-gradient-to-br ${theme.gradient} rounded-full p-3 shadow-lg`}>
                    <Award className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className={`${theme.primaryShades[50]} rounded-lg p-3 text-center border ${theme.border.primary}`}>
                    <Users className={`w-5 h-5 ${theme.text.primary} mx-auto mb-1`} />
                    <p className="text-xs text-gray-500 font-semibold">Teams</p>
                    <p className="text-lg font-black text-gray-800">{category.teams.length}</p>
                  </div>
                  <div className={`${theme.secondaryShades[50]} rounded-lg p-3 text-center border ${theme.border.secondary}`}>
                    <List className={`w-5 h-5 ${theme.text.secondary} mx-auto mb-1`} />
                    <p className="text-xs text-gray-500 font-semibold">Matches</p>
                    <p className="text-lg font-black text-gray-800">{totalMatches}</p>
                  </div>
                  <div className={`${theme.primaryShades[50]} rounded-lg p-3 text-center border ${theme.border.primary}`}>
                    <TrendingUp className={`w-5 h-5 ${theme.text.primary} mx-auto mb-1`} />
                    <p className="text-xs text-gray-500 font-semibold">Complete</p>
                    <p className="text-lg font-black text-gray-800">{completedMatches}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className={`${theme.primaryShades[100]} rounded-full h-2 overflow-hidden`}>
                  <div
                    className={`bg-gradient-to-r ${theme.gradient} h-full transition-all duration-500`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className={`text-xs ${theme.text.primary} text-right mt-1 font-bold`}>
                  {progress.toFixed(0)}% Complete
                </p>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default TournamentView
